import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import InaccessibleResources from "../../helpers/InaccessibleResources";
import GetKibanaDashboard from "../../components/Common/HelperComponents/KibanaDashboardComponents/GetKibanaDashboard";
import { Tab, Tabs, Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ApplicationsTagging from "./ApplicationsTagging";
import { isMarginManagementDashboardVisible } from "../../helpers/util_helper";
import { getMarginRecords } from "../../store/marginManagement/marginSlice";
import { useOrganization } from "../../components/Common/HelperComponents/GetDataFromStore";
import AnimatedLogo from "../../components/Common/HelperComponents/CloudCatcherAnimatedLogo/CloudCatcherAnimatedLogo";
import { OrgContext } from "../../OrgContext";
import { switchToOrganization } from "../MarginManagement/utils/organizationSwitcher";

const freeLicenseName = process.env.REACT_APP_FREE_ORGANIZATION_LICENSE;
const freeLicenses = [freeLicenseName];

// Main Cost Optimization Dashboard
const CostInsightDashboard = ({ licenseName, message }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { setHasSelectedOrganization } = useContext(OrgContext);
  const [activeTab, setActiveTab] = useState("cost-insight");
  const [cameFromMarginManagement, setCameFromMarginManagement] = useState(false);
  const isMarginDashboardVisible = isMarginManagementDashboardVisible();

  // Get orgCodes, msp, and mspOrgCode from Redux store
  const {
    orgCodes,
    msp,
    mspOrgCode,
  } = useSelector((state) => state.orgScope);

  // Get current organization
  const organization = useOrganization();

  // Get margin records from Redux store
  const { marginRecords, isFetchingMarginRecords, fetchMarginRecordsError } =
    useSelector((state) => state.margin);

  // Helper function to get date in YYYY-MM-DD format for comparison
  const getDateString = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Get active margin record
  const getActiveMarginRecord = () => {
    if (!marginRecords || marginRecords.length === 0) return null;

    const today = new Date();
    const todayStr = getDateString(today);

    return marginRecords.find((record) => {
      if (!record.effectiveStartDate || !record.effectiveEndDate) return false;

      const startDateStr = getDateString(new Date(record.effectiveStartDate));
      const endDateStr = getDateString(new Date(record.effectiveEndDate));

      return todayStr >= startDateStr && todayStr <= endDateStr;
    });
  };

  // Fetch margin records when organization is available
  useEffect(() => {
    // Only fetch margin records when we came from Margin Management.
    // If user navigated here via org selection / profile menu, default dashboards should use 30d.
    if (organization?.orgcode && cameFromMarginManagement) {
      console.log(
        "🔄 CostInsight: Fetching margin records for org:",
        organization.orgcode
      );
      dispatch(getMarginRecords(organization.orgcode));
    }
  }, [dispatch, organization?.orgcode, cameFromMarginManagement]);

  // Check if we came from Margin Management page - persist across tabs/pages
  useEffect(() => {
    const checkFlag = () => {
      const cameFromMargin = localStorage.getItem("cameFromMarginManagement") === "true";
      setCameFromMarginManagement(cameFromMargin);
    };

    // Check on mount
    checkFlag();

    // Listen for storage changes (in case flag is cleared elsewhere)
    const handleStorageChange = (e) => {
      if (e.key === "cameFromMarginManagement") {
        checkFlag();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case of same-tab changes
    const intervalId = setInterval(checkFlag, 100);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  // Clear flag when navigating to orgSelection or when going back to MSP
  useEffect(() => {
    const unlisten = history.listen((location) => {
      // Clear flag when navigating to orgSelection
      if (location.pathname === "/orgSelection") {
        localStorage.removeItem("cameFromMarginManagement");
        setCameFromMarginManagement(false);
      }
    });

    return unlisten;
  }, [history]);

  // Handle case where margin insight tab is hidden but was previously active
  useEffect(() => {
    if (activeTab === "marginInsight" && !isMarginDashboardVisible) {
      setActiveTab("cost-insight");
    }
  }, [activeTab, isMarginDashboardVisible]);

  // Handle back button click - switch back to MSP organization
  const handleBackToMarginManagement = async () => {
    try {
      // Get mspOrgCode from Redux store or localStorage
      const mspOrgCodeToUse = mspOrgCode || localStorage.getItem("mspOrgCode");
      
      if (!mspOrgCodeToUse) {
        console.error("🚀 ~ MSP OrgCode not found, cannot switch back");
        // Fallback: just navigate to margin management
        localStorage.removeItem("cameFromMarginManagement");
        history.push("/customer-management");
        return;
      }

      console.log("🚀 ~ Switching back to MSP organization:", mspOrgCodeToUse);

      // Use the organization switcher to switch back to MSP organization
      // Explicitly set msp to true since we're switching back to MSP organization
      const success = await switchToOrganization(mspOrgCodeToUse, {
        dispatch,
        history,
        setHasSelectedOrganization,
        orgCodes,
        msp: true, // Explicitly set to true when switching back to MSP organization
        mspOrgCode: mspOrgCodeToUse,
        redirectPath: "/customer-management", // Redirect to margin management page
      });

      if (success) {
        // Clear the flag after successful switch
        localStorage.removeItem("cameFromMarginManagement");
      } else {
        console.error("🚀 ~ Failed to switch back to MSP organization");
        // Fallback: just navigate to margin management
        localStorage.removeItem("cameFromMarginManagement");
        history.push("/customer-management");
      }
    } catch (error) {
      console.error("🚀 ~ Error switching back to Margin Management:", error);
      // Fallback: just navigate to margin management
      localStorage.removeItem("cameFromMarginManagement");
      history.push("/customer-management");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get active margin dates for dashboard filters
  const getActiveMarginDates = () => {
    // Only return margin dates if API was successful and we have an active record
    if (fetchMarginRecordsError) return null;

    const activeRecord = getActiveMarginRecord();
    if (!activeRecord) return null;

    return {
      startDate: activeRecord.effectiveStartDate,
      endDate: activeRecord.effectiveEndDate,
    };
  };

  // Determine which dashboard to use based on mspUser and organization relationship
  const getCostInsightDashboardKey = () => {
    try {
      // If we came from Margin Management, always use cur_2_dashboard
      if (cameFromMarginManagement) {
        return "cur_2_dashboard";
      }

      // Get mspUser from localStorage
      const mspUserStr = localStorage.getItem("mspUser");
      const mspUser = mspUserStr === "true";

      // Get selectedOrganization from localStorage
      const selectedOrgStr = localStorage.getItem("selectedOrganization");
      if (!selectedOrgStr) {
        // Default fallback
        return "cur_2_dashboard_orignal_cost";
      }

      const selectedOrg = JSON.parse(selectedOrgStr);
      const orgCode = selectedOrg?.orgCode;
      const mspOrgCode = selectedOrg?.mspOrgCode; // This is mspOrgCode from selectedOrganization

      // Case 3: mspUser === true AND mspOrgCode === null → cur_2_dashboard_orignal_cost
      // Check this FIRST before checking !== to avoid null !== orgCode matching
      if (mspUser && (mspOrgCode === null || mspOrgCode === undefined)) {
        return "cur_2_dashboard_orignal_cost";
      }

      // Case 4: mspUser === true AND mspOrgCode === orgCode → cur_2_dashboard_orignal_cost
      if (mspUser && mspOrgCode === orgCode) {
        return "cur_2_dashboard_orignal_cost";
      }

      // Case 1: mspUser === true AND mspOrgCode !== orgCode → cur_2_dashboard
      if (mspUser && mspOrgCode !== orgCode) {
        return "cur_2_dashboard";
      }

      // Case 2: mspUser === false AND mspOrgCode !== orgCode → cur_2_dashboard
      if (!mspUser && mspOrgCode !== orgCode) {
        return "cur_2_dashboard";
      }

      // Default fallback
      return "cur_2_dashboard_orignal_cost";
    } catch (error) {
      console.error("Error determining dashboard key:", error);
      return "cur_2_dashboard_orignal_cost";
    }
  };

  const renderTabContent = () => {
    // Only block rendering when we actually need margin dates (i.e., navigated from Margin Management)
    if (cameFromMarginManagement && isFetchingMarginRecords) {
      return (
        <div className="kibana-loading-container" id="kibanaDashboardLoading">
          <AnimatedLogo />
        </div>
      );
    }

    const marginDates = cameFromMarginManagement ? getActiveMarginDates() : null;
    const dashboardParams = cameFromMarginManagement ? marginDates : undefined;

    switch (activeTab) {
      case "cost-insight":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // Cost Insight dashboard selection logic:
          // 1. If cameFromMarginManagement → cur_2_dashboard (with margin dates)
          // 2. If mspUser === true AND mspOrgCode !== orgCode → cur_2_dashboard
          // 3. If mspUser === false AND mspOrgCode !== orgCode → cur_2_dashboard
          // 4. If mspUser === true AND mspOrgCode === null → cur_2_dashboard_orignal_cost
          // 5. If mspUser === true AND mspOrgCode === orgCode → cur_2_dashboard_orignal_cost
          const dashboardKey = getCostInsightDashboardKey();
          return (
            <GetKibanaDashboard
              dashboardKey={dashboardKey}
              params={dashboardParams}
            />
          );
        }
      case "tags":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // TagAllocation
          return <ApplicationsTagging />;
        }
      case "ri_sp":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // RI_SP
          return <GetKibanaDashboard dashboardKey={"RI/SP Dashboard"} />;
        }
      case "dataTransfer":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // DataTransfer
          return <GetKibanaDashboard dashboardKey={"Data Transfer"} />;
        }
      case "marginInsight":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // Cost Insight (W/O Margin):
          // - If we came from Margin Management, apply active margin dates (if any)
          // - Otherwise, let dashboards default to 30d (no margin date params)
          return (
            <GetKibanaDashboard
              dashboardKey={"cur_2_dashboard_orignal_cost"}
              params={dashboardParams}
            />
          );
        }
      case "eksSummary":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // EKS Summary
          return <GetKibanaDashboard dashboardKey={"EKS Cluster"} />;
        }
      case "aws_connect_dashboard":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          return (
            <GetKibanaDashboard
              dashboardKey="aws_connect_dashboard"
              params={dashboardParams}
            />
          );
        }
      case "marketplaceInsights":
        if (freeLicenses.includes(licenseName)) {
          return <InaccessibleResources message={message} />;
        } else {
          // AWS Marketplace Insights
          return <GetKibanaDashboard dashboardKey={"marketplace_dashboard"} />;
        }
      default:
        return null;
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0, // Allow shrinking below content size
            overflow: "hidden", // Prevent overflow
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-scrollButtons": {
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
            }}
          >
            <Tab label="Cloud Spend" value="cost-insight" />
            <Tab label="RI/SP" value="ri_sp" />
            <Tab label="Data Transfer" value="dataTransfer" />
            <Tab label="EKS Summary" value="eksSummary" />
            <Tab label="AWS Direct Connect Dashboard" value="aws_connect_dashboard" />
            <Tab label="AWS Marketplace Insights" value="marketplaceInsights" />
            {/* Only show Margin Insight tab for MSP users in NON-MSP organizations */}
            {isMarginDashboardVisible && (
              <Tab label="Cloud Spend (MSP)" value="marginInsight" />
            )}
            {/* <Tab label="Applications Tagging" value="tags" /> */}
          </Tabs>
        </Box>
        {/* Back button - only show if we came from Margin Management */}
        {cameFromMarginManagement && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToMarginManagement}
            sx={{
              ml: 2,
              mr: 2,
              flexShrink: 0, // Prevent button from shrinking
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "white",
                borderColor: "primary.main",
              },
            }}
          >
            Back to Customer Management
          </Button>
        )}
      </Box>
      {renderTabContent()}
    </div>
  );
};

export default CostInsightDashboard;
